import { useState } from 'react';
import { Platform, Share, Switch, View } from 'react-native';
import { router } from 'expo-router';
import * as Clipboard from 'expo-clipboard';
import QRCode from 'react-native-qrcode-svg';
import { ArrowLeftRight, CalendarDays, Download, LifeBuoy, Mail, MonitorSmartphone, House, QrCode, Languages, Link2, LogOut, RefreshCw, Send, Sprout, Trash2, UserPlus, UserRound } from 'lucide-react-native';
import { brand, colors, crewLabels, LANGUAGE_NAMES, roleLabels, ts, weekdayShort, WORKER_LANGS, type ApiStaff, type Crew, type WorkerLang } from '@hogar/shared';
import { Button, Card, Field, RowLink, Screen, SectionTitle, Segmented, Txt, confirm, notice, showProblem, useT } from '../../components/ui';
import { downloadMyData } from '../../lib/download';
import { LanguagePicker } from '../../components/LanguagePicker';
import { api, ApiError } from '../../lib/api';
import { useAction, useSession } from '../../lib/session';
import { useHouse } from '../../lib/house';
import { hasGarden } from '../../lib/crew';

export default function More() {
  const { t, lang } = useT();
  const { setLang, signOut } = useSession();
  const house = useHouse();
  const { household, staff, owners, owner, invitation, rooms } = house;
  const garden = hasGarden(house);
  const gardenAreas = rooms.some((r) => r.crew === 'garden');
  const [newCrew, setNewCrew] = useState<Crew>('house');
  const [newLang, setNewLang] = useState<WorkerLang>('es');
  const [inviteEmail, setInviteEmail] = useState('');
  const act = useAction();
  const [newStaff, setNewStaff] = useState('');
  const [shown, setShown] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);

  const days = household.workDays.map((d) => weekdayShort(d, lang)).join(' · ');
  const gardenDays = household.garden.workDays.map((d) => weekdayShort(d, lang)).join(' · ');

  /** Opens the phone's share sheet (WhatsApp, Messages...). Where that is not available, copies or shows the link. */
  const send = async (s: ApiStaff) => {
    const message = `${ts(s.lang, 'shareMessage', { name: s.firstName, brand: brand.name })} ${s.link}`;
    if (Platform.OS !== 'web') {
      await Share.share({ message });
      return;
    }
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    if (nav.share) {
      await nav.share({ text: message }).catch(() => {});
      return;
    }
    await copy(s);
  };

  const copy = async (s: ApiStaff) => {
    try {
      await Clipboard.setStringAsync(s.link);
      notice(t('linkCopied'));
    } catch {
      // Phones only allow copying from a secure address; show the link instead.
      setShown(s.id);
    }
  };

  const renew = async (s: ApiStaff) => {
    if (!(await confirm(t('newLinkConfirm'), t('newLink'), t('cancel')))) return;
    await act(() => api('POST', `/staff/${s.id}/new-link`));
  };

  const remove = async (s: ApiStaff) => {
    if (!(await confirm(t('removeStaffConfirm', { name: s.firstName }), t('removeStaff'), t('cancel')))) return;
    await act(() => api('DELETE', `/staff/${s.id}`));
  };

  const invite = async () => {
    try {
      await act(() => api('POST', '/owners/invite', { email: inviteEmail.trim() }));
      setInviteEmail('');
    } catch (e) {
      const code = e instanceof ApiError ? e.code : '';
      notice(code === 'owner_limit' ? t('ownerLimit') : code === 'has_household' ? t('hasHousehold') : code.startsWith('invalid_input') ? t('errEmail') : t('errGeneric'));
    }
  };

  const removeOwner = async (id: string, name: string) => {
    if (!(await confirm(t('removeOwnerConfirm', { name }), t('removeStaff'), t('cancel')))) return;
    await act(() => api('DELETE', `/owners/${id}`));
  };

  const deleteAccount = async () => {
    if (!(await confirm(t('deleteAccountConfirm'), t('delete'), t('cancel')))) return;
    await api('DELETE', '/me');
    await signOut();
  };

  const add = async () => {
    const firstName = newStaff.trim();
    if (!firstName) return;
    // The first gardener also sets up the garden: its days, hours, and areas.
    if (newCrew === 'garden' && !gardenAreas) {
      router.push({ pathname: '/garden-setup', params: { name: firstName, lang: newLang } });
      setNewStaff('');
      return;
    }
    await act(() => api('POST', '/staff', { firstName, crew: newCrew, lang: newLang }));
    setNewStaff('');
  };

  /** Moves someone between the house and the garden (for example a gardener first added as house staff). */
  const move = async (s: ApiStaff) => {
    const to: Crew = s.crew === 'house' ? 'garden' : 'house';
    if (to === 'garden' && !gardenAreas) return router.push({ pathname: '/garden-setup', params: { staffId: s.id } });
    await act(() => api('PATCH', `/staff/${s.id}`, { crew: to }));
  };

  return (
    <Screen title={t('settings')}>
      <Card>
        <RowLink Icon={House} title={household.name} subtitle={t('houseName')} onPress={() => router.push('/house-edit')} />
        <RowLink Icon={CalendarDays} title={garden ? `${t('workDaysHours')} · ${crewLabels.house[lang]}` : t('workDaysHours')} subtitle={`${days}, ${household.workStart}–${household.workEnd}`} onPress={() => router.push('/house-edit')} last={!garden} />
        {garden && (
          <RowLink Icon={Sprout} title={t('gardenDaysHours')} subtitle={gardenDays ? `${gardenDays}, ${household.garden.workStart}–${household.garden.workEnd}` : '—'} onPress={() => router.push({ pathname: '/house-edit', params: { crew: 'garden' } })} last />
        )}
      </Card>

      <SectionTitle>{t('staff')}</SectionTitle>
      {staff.map((s) => (
        <Card key={s.id}>
          <RowLink Icon={UserRound} title={s.firstName} subtitle={`${roleLabels[s.crew][lang]} · ${LANGUAGE_NAMES[s.lang]} · ${s.linkActive ? t('linkActive') : t('linkOff')}`} subtitleColor={s.linkActive ? colors.okText : colors.dangerText} right={<View />} />
          {s.linkActive && <RowLink Icon={Send} title={t('sendLink')} onPress={() => send(s)} />}
          {s.linkActive && <RowLink Icon={QrCode} title={t('showQr')} onPress={() => setQr(qr === s.id ? null : s.id)} />}
          {qr === s.id && (
            <View style={{ alignItems: 'center', paddingVertical: 16, gap: 10 }}>
              <QRCode value={s.link} size={220} color={colors.text} backgroundColor={colors.white} />
              <Txt size={13} color={colors.textMuted} style={{ textAlign: 'center' }}>
                {t('qrHelp')}
              </Txt>
            </View>
          )}
          {shown === s.id && (
            <Txt size={13} selectable style={{ paddingVertical: 10 }}>
              {s.link}
            </Txt>
          )}
          <RowLink Icon={RefreshCw} title={t('newLink')} onPress={() => renew(s)} />
          <View style={{ paddingVertical: 10, gap: 6 }}>
            <Txt size={13} color={colors.textMuted}>{t('workerLanguage')}</Txt>
            <LanguagePicker list value={s.lang} options={WORKER_LANGS} onChange={(v) => act(() => api('PATCH', `/staff/${s.id}`, { lang: v }))} />
          </View>
          <RowLink Icon={ArrowLeftRight} title={t('moveToRole', { role: roleLabels[s.crew === 'house' ? 'garden' : 'house'][lang] })} onPress={() => move(s)} />
          <RowLink
            Icon={Link2}
            title={s.linkActive ? t('turnOffLink') : t('turnOnLink')}
            onPress={() => act(() => api('PATCH', `/staff/${s.id}`, { linkActive: !s.linkActive }))}
          />
          <RowLink Icon={Trash2} iconTint={colors.danger} iconBg={colors.dangerSoft} title={t('removeStaff')} onPress={() => remove(s)} last />
        </Card>
      ))}
      <Card style={{ paddingVertical: 12, gap: 10 }}>
        <Field value={newStaff} onChange={setNewStaff} placeholder={t('firstName')} onSubmit={add} />
        <Txt size={13} color={colors.textMuted}>{t('staffRoleLabel')}</Txt>
        <Segmented<Crew> value={newCrew} options={[{ value: 'house', label: roleLabels.house[lang] }, { value: 'garden', label: roleLabels.garden[lang] }]} onChange={setNewCrew} />
        <Txt size={13} color={colors.textMuted}>{t('workerLanguage')}</Txt>
        <LanguagePicker list value={newLang} options={WORKER_LANGS} onChange={setNewLang} />
        <Button label={t('addStaff')} Icon={UserPlus} variant="outline" onPress={add} disabled={!newStaff.trim()} />
      </Card>

      <SectionTitle>{t('language')}</SectionTitle>
      <LanguagePicker value={lang} options={['es', 'en'] as const} onChange={setLang} />


      <View style={{ height: 14 }} />
      <Card>
        <RowLink
          Icon={Mail}
          title={t('reportByEmail')}
          subtitle={t('reportByEmailSub')}
          right={<Switch value={owner.reportEmails} onValueChange={(v) => act(() => api('PATCH', '/me', { reportEmails: v }))} trackColor={{ true: colors.primary, false: colors.cardBorder }} />}
          last
        />
      </Card>
      <Card>
        <RowLink Icon={Languages} title={t('translations')} subtitle={t('translationsSub', { used: household.translationsUsed, limit: household.translationLimit })} last />
      </Card>

      <SectionTitle>{t('account')}</SectionTitle>
      <Card>
        {owners.map((o) =>
          o.id === owner.id ? (
            <RowLink key={o.id} Icon={UserRound} title={`${o.name} (${t('you')})`} subtitle={owner.email} />
          ) : (
            <RowLink key={o.id} Icon={UserRound} title={o.name} subtitle={t('removeOwner')} subtitleColor={colors.dangerText} onPress={() => removeOwner(o.id, o.name)} />
          ),
        )}
        <RowLink Icon={MonitorSmartphone} title={t('devices')} subtitle={t('devicesSub')} onPress={() => router.push('/devices')} />
        <RowLink Icon={LogOut} title={t('signOut')} onPress={() => api('POST', '/auth/signout').catch(() => {}).finally(signOut)} />
        <RowLink
          Icon={LogOut}
          title={t('signOutAll')}
          onPress={async () => {
            if (!(await confirm(t('signOutAllConfirm'), t('signOut'), t('cancel')))) return;
            await api('POST', '/auth/signout-all').catch(() => {});
            await signOut();
          }}
          last
        />
      </Card>

      {owners.length < 2 && (
        <>
          <SectionTitle>{t('secondOwner')}</SectionTitle>
          {invitation ? (
            <Card style={{ paddingVertical: 12, gap: 10 }}>
              <Txt size={14}>{t('invitePending', { email: invitation.email })}</Txt>
              <Button label={t('cancelInvite')} variant="outline" onPress={() => act(() => api('DELETE', '/owners/invite'))} />
            </Card>
          ) : (
            <Card style={{ paddingVertical: 12, gap: 10 }}>
              <Txt size={14} color={colors.textMuted}>
                {t('secondOwnerSub')}
              </Txt>
              <Field value={inviteEmail} onChange={setInviteEmail} keyboardType="email-address" placeholder={t('inviteEmail')} />
              <Button label={t('invite')} Icon={UserPlus} variant="outline" onPress={invite} disabled={!inviteEmail.includes('@')} />
            </Card>
          )}
        </>
      )}

      <Card style={{ marginTop: 14 }}>
        <RowLink Icon={LifeBuoy} title={t('help')} subtitle={t('helpSub')} onPress={() => router.push('/help')} />
        <RowLink Icon={Download} title={t('downloadData')} subtitle={t('downloadDataSub')} onPress={() => downloadMyData().catch(() => showProblem(t('errGeneric'), lang))} />
        <RowLink Icon={Trash2} iconTint={colors.danger} iconBg={colors.dangerSoft} title={t('deleteAccount')} subtitle={t('deleteAccountSub')} onPress={deleteAccount} last />
      </Card>
    </Screen>
  );
}
